import { useState, useEffect, useCallback, ComponentType, SVGProps } from 'react';
import { getAccessToken } from '@lib/msal';
import {
  Settings,
  AlertTriangle,
  Clock,
  PoundSterling,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Save,
  RefreshCw,
  Wallet,
  PiggyBank,
  Building2,
  CreditCard,
  Landmark,
  Banknote,
  CircleDollarSign,
  BadgePoundSterling,
  Receipt,
  X,
  LucideIcon,
  Link,
  ExternalLink,
} from 'lucide-react';
import { PayPalIcon, StripeIcon, NatWestIcon, StarlingIcon, MonzoIcon, RevolutIcon } from '@components/icons/BrandIcons';
import ZapierAutomationSettings from '@components/settings/ZapierAutomationSettings';

// Type for any icon component
type IconComponent = LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;

interface SystemSettings {
  maintenanceMode: boolean;
  maintenanceMessage?: string;
  transactionDays: number;
  watchedAccountId?: string;
  lastUpdatedBy?: string;
  lastUpdatedAt?: string;
}

interface FreeAgentAccountIcon {
  accountId: string;
  accountName: string;
  iconName: string;
  colour?: string;
}

interface FreeAgentIconSettings {
  accountIcons: FreeAgentAccountIcon[];
  lastUpdatedBy?: string;
  lastUpdatedAt?: string;
}

interface BankAccount {
  id: string;
  name: string;
  type: string;
  status: string;
}

// Available icons for bank accounts - grouped by type
const availableIcons: { name: string; icon: IconComponent; description: string; branded?: boolean }[] = [
  // Branded / Company icons
  { name: 'PayPal', icon: PayPalIcon, description: 'PayPal', branded: true },
  { name: 'Stripe', icon: StripeIcon, description: 'Stripe', branded: true },
  { name: 'NatWest', icon: NatWestIcon, description: 'NatWest', branded: true },
  { name: 'Starling', icon: StarlingIcon, description: 'Starling Bank', branded: true },
  { name: 'Monzo', icon: MonzoIcon, description: 'Monzo', branded: true },
  { name: 'Revolut', icon: RevolutIcon, description: 'Revolut', branded: true },
  // Generic icons
  { name: 'Wallet', icon: Wallet, description: 'General wallet' },
  { name: 'PiggyBank', icon: PiggyBank, description: 'Savings account' },
  { name: 'Building2', icon: Building2, description: 'Business/Bank' },
  { name: 'CreditCard', icon: CreditCard, description: 'Card account' },
  { name: 'Landmark', icon: Landmark, description: 'Main bank' },
  { name: 'Banknote', icon: Banknote, description: 'Cash/Notes' },
  { name: 'CircleDollarSign', icon: CircleDollarSign, description: 'Currency' },
  { name: 'BadgePoundSterling', icon: BadgePoundSterling, description: 'GBP account' },
  { name: 'Receipt', icon: Receipt, description: 'Transactions' },
  { name: 'PoundSterling', icon: PoundSterling, description: 'Sterling' },
];

// Get icon component by name
function getIconByName(name: string): IconComponent {
  const iconDef = availableIcons.find(i => i.name === name);
  return iconDef?.icon || Wallet;
}

// Transaction period options
const transactionPeriodOptions = [
  { value: 7, label: '1 week' },
  { value: 14, label: '2 weeks' },
  { value: 30, label: '1 month' },
  { value: 60, label: '2 months' },
  { value: 90, label: '3 months' },
];

export default function SystemSettings() {

  // System settings state
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState(false);

  // FreeAgent icons state
  const [iconSettings, setIconSettings] = useState<FreeAgentIconSettings | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [isLoadingIcons, setIsLoadingIcons] = useState(true);
  const [isSavingIcons, setIsSavingIcons] = useState(false);
  const [iconsError, setIconsError] = useState<string | null>(null);
  const [iconsSuccess, setIconsSuccess] = useState(false);

  // Icon picker state
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);


  // Local form state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [transactionDays, setTransactionDays] = useState(30);
  const [watchedAccountId, setWatchedAccountId] = useState<string>('');
  const [localIconSettings, setLocalIconSettings] = useState<FreeAgentAccountIcon[]>([]);

  const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');

  // Fetch system settings
  const fetchSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);

    try {
      const token = await getAccessToken();
      const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${apiBase}/api/settings/system`, { headers: authHeaders });
      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      setSettings(data.data);
      setMaintenanceMode(data.data.maintenanceMode || false);
      setMaintenanceMessage(data.data.maintenanceMessage || '');
      setTransactionDays(data.data.transactionDays || 30);
      setWatchedAccountId(data.data.watchedAccountId || '');
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoadingSettings(false);
    }
  }, [apiBase]);

  // Fetch FreeAgent icon settings and bank accounts
  const fetchIconSettings = useCallback(async () => {
    setIsLoadingIcons(true);
    setIconsError(null);

    try {
      const token = await getAccessToken();
      const authHeaders: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch icon settings
      const iconsResponse = await fetch(`${apiBase}/api/settings/freeagent-icons`, { headers: authHeaders });
      if (!iconsResponse.ok) throw new Error('Failed to fetch icon settings');
      const iconsData = await iconsResponse.json();
      setIconSettings(iconsData.data);
      setLocalIconSettings(iconsData.data.accountIcons || []);

      // Fetch bank accounts from FreeAgent
      const accountsResponse = await fetch(`${apiBase}/api/freeagent/overview`, { headers: authHeaders });
      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        if (accountsData.data?.bankAccounts) {
          setBankAccounts(accountsData.data.bankAccounts);
        }
      }
    } catch (err) {
      setIconsError(err instanceof Error ? err.message : 'Failed to load icon settings');
    } finally {
      setIsLoadingIcons(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchSettings();
    fetchIconSettings();
  }, [fetchSettings, fetchIconSettings]);

  // Save system settings
  const saveSettings = async () => {
    setIsSavingSettings(true);
    setSettingsError(null);
    setSettingsSuccess(false);

    try {
      const token = await getAccessToken();
      const response = await fetch(`${apiBase}/api/settings/system`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          maintenanceMode,
          maintenanceMessage: maintenanceMessage || undefined,
          transactionDays,
          watchedAccountId: watchedAccountId || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save settings');
      }

      setSettings(data.data);
      setSettingsSuccess(true);
      setTimeout(() => setSettingsSuccess(false), 3000);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Save icon settings
  const saveIconSettings = async () => {
    setIsSavingIcons(true);
    setIconsError(null);
    setIconsSuccess(false);

    try {
      const token = await getAccessToken();
      const response = await fetch(`${apiBase}/api/settings/freeagent-icons`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          accountIcons: localIconSettings,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save icon settings');
      }

      setIconSettings(data.data);
      setIconsSuccess(true);
      setTimeout(() => setIconsSuccess(false), 3000);
    } catch (err) {
      setIconsError(err instanceof Error ? err.message : 'Failed to save icon settings');
    } finally {
      setIsSavingIcons(false);
    }
  };

  // Update icon for an account
  const updateAccountIcon = (accountId: string, accountName: string, iconName: string) => {
    setLocalIconSettings(prev => {
      const existing = prev.findIndex(i => i.accountId === accountId);
      if (existing >= 0) {
        const existingItem = prev[existing];
        if (existingItem) {
          const updated = [...prev];
          updated[existing] = {
            accountId: existingItem.accountId,
            accountName: existingItem.accountName,
            iconName,
          };
          return updated;
        }
      }
      return [...prev, { accountId, accountName, iconName }];
    });
    setEditingAccountId(null);
  };

  // Get icon for an account
  const getAccountIcon = (accountId: string) => {
    const setting = localIconSettings.find(i => i.accountId === accountId);
    return setting?.iconName || 'Wallet';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-sca-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading system settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        </div>
        <button
          onClick={() => { fetchSettings(); fetchIconSettings(); }}
          className="btn-secondary text-sm py-1.5"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Refresh
        </button>
      </div>

      {/* Maintenance Mode */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <h2 className="text-lg font-semibold text-gray-900">Maintenance Mode</h2>
        </div>

        {settingsError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{settingsError}</span>
          </div>
        )}

        {settingsSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">Settings saved successfully!</span>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Enable Maintenance Mode</p>
              <p className="text-sm text-gray-500">When enabled, only admins can access the system</p>
            </div>
            <button
              type="button"
              onClick={() => setMaintenanceMode(!maintenanceMode)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-sca-purple-500 focus:ring-offset-2 ${
                maintenanceMode ? 'bg-amber-500' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={maintenanceMode}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                  maintenanceMode ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {maintenanceMode && (
            <div>
              <label className="label">Maintenance Message (optional)</label>
              <textarea
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="The system is currently undergoing scheduled maintenance..."
                rows={3}
                className="input"
              />
              <p className="text-xs text-gray-500 mt-1">
                This message will be shown to non-admin users
              </p>
            </div>
          )}

          {settings?.lastUpdatedAt && (
            <p className="text-xs text-gray-400">
              Last updated: {formatDate(settings.lastUpdatedAt)}
              {settings.lastUpdatedBy && ` by ${settings.lastUpdatedBy}`}
            </p>
          )}
        </div>
      </div>

      {/* FreeAgent Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Clock className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900">FreeAgent Settings</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">Recent Transactions Period</label>
            <select
              value={transactionDays}
              onChange={(e) => setTransactionDays(Number(e.target.value))}
              className="input max-w-xs"
            >
              {transactionPeriodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How far back to fetch transactions on the Finance page
            </p>
          </div>

          <div>
            <label className="label">Watched Account (Debit Card Transactions)</label>
            {isLoadingIcons ? (
              <p className="text-sm text-gray-400">Loading accounts...</p>
            ) : (
              <select
                value={watchedAccountId}
                onChange={(e) => setWatchedAccountId(e.target.value)}
                className="input max-w-xs"
              >
                <option value="">— Not configured —</option>
                {bankAccounts
                  .filter(acc => acc.status?.toLowerCase() === 'active')
                  .map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Unexplained transactions on this account whose description starts with a
              team member's debit card number will be attributed to them on the Finance page and Dashboard
            </p>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={saveSettings}
            disabled={isSavingSettings}
            className="btn-primary"
          >
            {isSavingSettings ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* FreeAgent Account Icons */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <PoundSterling className="h-5 w-5 text-green-500" />
          <h2 className="text-lg font-semibold text-gray-900">FreeAgent Account Icons</h2>
        </div>

        {iconsError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{iconsError}</span>
          </div>
        )}

        {iconsSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">Icon settings saved successfully!</span>
          </div>
        )}

        {isLoadingIcons ? (
          <div className="py-8 text-center">
            <Loader2 className="h-6 w-6 text-sca-purple-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading accounts...</p>
          </div>
        ) : bankAccounts.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-gray-500">No bank accounts found in FreeAgent</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">
              Customise the icon shown for each bank account on the Finance page
            </p>

            {bankAccounts.map((account) => {
              const currentIconName = getAccountIcon(account.id);
              const CurrentIcon = getIconByName(currentIconName);
              const isEditing = editingAccountId === account.id;

              return (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg border border-gray-200">
                      <CurrentIcon className="h-5 w-5 text-sca-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{account.name}</p>
                      <p className="text-xs text-gray-500">
                        {account.type} • {account.status}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Show current icon name */}
                    <span className="text-xs text-gray-400">
                      {availableIcons.find(i => i.name === currentIconName)?.description || currentIconName}
                    </span>

                    <div className="relative">
                      <button
                        onClick={() => setEditingAccountId(isEditing ? null : account.id)}
                        className="btn-secondary text-sm py-1.5"
                      >
                        {isEditing ? (
                          <>
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </>
                        ) : (
                          'Change Icon'
                        )}
                      </button>

                      {/* Icon Picker Dropdown */}
                      {isEditing && (
                        <div className="absolute right-0 top-full mt-2 p-4 bg-white rounded-lg shadow-lg border border-gray-200 z-10 w-80">
                          {/* Branded Icons Section */}
                          <p className="text-xs font-semibold text-gray-700 mb-2">Company / Brand</p>
                          <div className="grid grid-cols-3 gap-2 mb-4">
                            {availableIcons.filter(i => i.branded).map((iconDef) => {
                              const Icon = iconDef.icon;
                              const isSelected = currentIconName === iconDef.name;

                              return (
                                <button
                                  key={iconDef.name}
                                  onClick={() => updateAccountIcon(account.id, account.name, iconDef.name)}
                                  className={`flex items-center gap-2 p-2 rounded-lg transition-colors text-left ${
                                    isSelected
                                      ? 'bg-sca-purple-100 text-sca-purple-600 ring-2 ring-sca-purple-500'
                                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                  }`}
                                  title={iconDef.description}
                                >
                                  <Icon className="h-4 w-4 flex-shrink-0" />
                                  <span className="text-xs truncate">{iconDef.description}</span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Generic Icons Section */}
                          <p className="text-xs font-semibold text-gray-700 mb-2">Generic</p>
                          <div className="grid grid-cols-5 gap-2">
                            {availableIcons.filter(i => !i.branded).map((iconDef) => {
                              const Icon = iconDef.icon;
                              const isSelected = currentIconName === iconDef.name;

                              return (
                                <button
                                  key={iconDef.name}
                                  onClick={() => updateAccountIcon(account.id, account.name, iconDef.name)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    isSelected
                                      ? 'bg-sca-purple-100 text-sca-purple-600 ring-2 ring-sca-purple-500'
                                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                  }`}
                                  title={iconDef.description}
                                >
                                  <Icon className="h-5 w-5" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {iconSettings?.lastUpdatedAt && (
              <p className="text-xs text-gray-400 mt-4">
                Last updated: {formatDate(iconSettings.lastUpdatedAt)}
                {iconSettings.lastUpdatedBy && ` by ${iconSettings.lastUpdatedBy}`}
              </p>
            )}
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={saveIconSettings}
            disabled={isSavingIcons || isLoadingIcons}
            className="btn-primary"
          >
            {isSavingIcons ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Icon Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* Connected Services */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Link className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold text-gray-900">Connected Services</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          External services integrated with the Operations Hub.
        </p>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Beacon CRM</p>
              <p className="text-sm text-gray-500">People and organisation data</p>
            </div>
            <a
              href="https://app.beaconcrm.org"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm py-1.5"
            >
              Open
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">FreeAgent</p>
              <p className="text-sm text-gray-500">Financial data (read-only)</p>
            </div>
            <a
              href="https://scauk.freeagent.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm py-1.5"
            >
              Open
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">MailChimp</p>
              <p className="text-sm text-gray-500">Email campaign data</p>
            </div>
            <a
              href="https://mailchimp.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm py-1.5"
            >
              Open
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium text-gray-900">Zapier</p>
              <p className="text-sm text-gray-500">Workflow automations via webhooks</p>
            </div>
            <a
              href="https://zapier.com/app/zaps"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm py-1.5"
            >
              Open
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </div>
        </div>
      </div>

      {/* Zapier Automations */}
      <div className="card">
        <ZapierAutomationSettings />
      </div>
    </div>
  );
}
